import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getDiatonicFunction } from './diatonic.js'

/**
 * Returns `true` when `(previous, current, next)` forms a ii-V-I pattern in
 * `scale`. Pure: reads only diatonic-function labels and never mutates input.
 *
 * Why a window helper: the analyzer evaluates one chord at a time, but the
 * ii-V-I tag is a property of the *position* in a 3-chord window. Callers
 * (currently `analyzeChord` under `style: 'jazz'`) pass the immediate
 * neighbors via `AnalysisContext.previous` / `.next`.
 *
 * Detection is chord-function-based, not root-based. This means
 *
 *   Dm7 → G7 → Cmaj7    (in C major)
 *   Dm   → G   → C      (plain triads)
 *
 * both match, since `getDiatonicFunction` reduces extensions to triads and
 * looks up the diatonic role. Dominant-7 on V (G7) is also accepted because
 * `getDiatonicFunction` returns `'V'` for `G7` in C major — the dom7-on-V
 * carve-out lives in `diatonic.ts`.
 *
 * Strict matching: all three positions must resolve to a diatonic ii / V / I.
 * Out-of-key chords short-circuit to `false` — no "almost a ii-V-I" reading.
 */
export const isIIVIPattern = (previous: Chord, current: Chord, next: Chord, scale: Scale): boolean => {
  const prevFn = getDiatonicFunction(previous, scale)
  const currFn = getDiatonicFunction(current, scale)
  const nextFn = getDiatonicFunction(next, scale)
  return prevFn === 'ii' && currFn === 'V' && nextFn === 'I'
}
